import { Chip, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatLoader from 'src/components/loading/chat-loader.component';
import StatusSwitch from 'src/components/switch/status-switch.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { DomainSummary, getHelpBaseUrl, KBSetupShell } from './kbSetupShared';

export default function KBSetup() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [loader, setLoader] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [kbSettings, setKBSettings] = useState({ is_enabled: false });
  const [collections, setCollections] = useState([]);
  const [articles, setArticles] = useState([]);
  const toggleInFlightRef = useRef(false);

  const canManageKB = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.KB_MANAGE
  );
  const helpBaseUrl = useMemo(() => getHelpBaseUrl(), []);
  const setupDisabled = !kbSettings.is_enabled;
  const disabledContentSx = setupDisabled
    ? {
        opacity: 0.45,
        filter: 'grayscale(0.35)',
        transition: 'opacity 180ms ease, filter 180ms ease',
      }
    : {
        opacity: 1,
        transition: 'opacity 180ms ease, filter 180ms ease',
      };

  // Stable fetch — do NOT depend on showToast (avoids re-fetch flicker)
  const fetchKBData = useCallback(() => {
    if (!canManageKB) {
      setLoader(false);
      return;
    }
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.kbCollections,
      successCallback: (collectionData) => {
        HttpServices.Get({
          url: userModuleURL.kbArticles,
          successCallback: (articleData) => {
            setCollections(Array.isArray(collectionData) ? collectionData : []);
            setArticles(Array.isArray(articleData) ? articleData : []);
            setLoader(false);
          },
          failureCallback: (message) => {
            setLoader(false);
            message && showToastRef.current(message, 'e');
          },
        });
      },
      failureCallback: (message) => {
        setLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, [canManageKB]);

  useEffect(() => {
    fetchKBData();
  }, [fetchKBData]);

  const fetchKBSettings = useCallback(() => {
    if (!canManageKB) return;

    HttpServices.Get({
      url: userModuleURL.kbSettings,
      successCallback: (data) => {
        setKBSettings({ is_enabled: data?.is_enabled === true });
      },
      failureCallback: (message) => {
        message && showToastRef.current(message, 'e');
      },
    });
  }, [canManageKB]);

  useEffect(() => {
    fetchKBSettings();
  }, [fetchKBSettings]);

  const toggleKBSettings = useCallback((event) => {
    if (toggleInFlightRef.current) return;

    const nextEnabled = event.target.checked;

    // Capture previous value for rollback
    let previousEnabled = true;
    setKBSettings((prev) => {
      previousEnabled = Boolean(prev.is_enabled);
      return prev.is_enabled === nextEnabled ? prev : { ...prev, is_enabled: nextEnabled };
    });

    toggleInFlightRef.current = true;
    setIsSavingSettings(true);

    HttpServices.Post({
      url: userModuleURL.kbSettings,
      body: { is_enabled: nextEnabled },
      successCallback: (data, message) => {
        const enabled = data?.is_enabled === true;
        setKBSettings((prev) =>
          prev.is_enabled === enabled ? prev : { ...prev, is_enabled: enabled }
        );
        toggleInFlightRef.current = false;
        setIsSavingSettings(false);
        message && showToastRef.current(message, 's');
      },
      failureCallback: (message) => {
        setKBSettings((prev) =>
          prev.is_enabled === previousEnabled ? prev : { ...prev, is_enabled: previousEnabled }
        );
        toggleInFlightRef.current = false;
        setIsSavingSettings(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, []);

  const copyHelpDomain = useCallback(() => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(helpBaseUrl);
    showToastRef.current(
      translate('kb_setup_page.domain_copied', { defaultValue: 'Knowledge base URL copied' }),
      's'
    );
  }, [helpBaseUrl, translate]);

  // Memoize header so Switch is not remounted every render
  const headerAction = useMemo(() => {
    if (!canManageKB) return null;

    return (
      <StatusSwitch
        checked={Boolean(kbSettings.is_enabled)}
        onChange={toggleKBSettings}
        // Never disable — disabling causes visible flicker
        inputProps={{
          'aria-label': kbSettings.is_enabled
            ? translate('kb_setup_page.disable_kb', { defaultValue: 'Disable Knowledge Base' })
            : translate('kb_setup_page.enable_kb', { defaultValue: 'Enable Knowledge Base' }),
        }}
        switchSx={{
          // Soft opacity while saving — no layout shift
          opacity: isSavingSettings ? 0.7 : 1,
          transition: 'opacity 0.15s ease',
        }}
      />
    );
  }, [canManageKB, kbSettings.is_enabled, isSavingSettings, toggleKBSettings, translate]);

  return (
    <KBSetupShell
      activeView="kb"
      title={translate('kb_setup_page.title', { defaultValue: 'Knowledge Base Setup' })}
      headerAction={headerAction}
    >
      {loader ? (
        <ChatLoader
          label={translate('kb_setup_page.loading', { defaultValue: 'Loading knowledge base' })}
          description={translate('kb_setup_page.loading_description', {
            defaultValue: 'Preparing your help center content',
          })}
        />
      ) : !canManageKB ? (
        <Typography variant="body2" color="text.secondary">
          {translate('kb_setup_page.permission_note', {
            defaultValue: 'Knowledge base setup is limited to users with KB manage access.',
          })}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, ...disabledContentSx }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1">
                  {translate('kb_setup_page.kb_setup_section', { defaultValue: 'KB Setup' })}
                </Typography>
                <DomainSummary
                  helpBaseUrl={helpBaseUrl}
                  disabled={setupDisabled}
                  onCopy={copyHelpDomain}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, ...disabledContentSx }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1">
                  {translate('kb_setup_page.status_title', { defaultValue: 'Knowledge Base Status' })}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={translate('kb_setup_page.collection_count', {
                      count: collections.length,
                      defaultValue: '{{count}} Collections',
                    })}
                  />
                  <Chip
                    label={translate('kb_setup_page.article_count', {
                      count: articles.length,
                      defaultValue: '{{count}} Articles',
                    })}
                  />
                  <Chip
                    color="primary"
                    label={translate('kb_setup_page.published_count', {
                      count: articles.filter((article) => article.status === 'published').length,
                      defaultValue: '{{count}} Published',
                    })}
                  />
                </Stack>
                <Divider />
                <Typography variant="body2" color="text.secondary">
                  {translate('kb_setup_page.status_help', {
                    defaultValue:
                      'Articles will be shown under your Brand2Chat app domain. No custom domain setup is required.',
                  })}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </KBSetupShell>
  );
}
